import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { InvoiceService } from '../../features/invoice/services/invoice.service';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
  selector: 'app-invoice-details',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatTabsModule,
    MatMenuModule,
    MatIconModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './invoice-details.component.html',
  styleUrl: './invoice-details.component.scss',
})
export class InvoiceDetailsComponent implements OnInit {
  selectedTabIndex = 0;
  invoiceId: any;
  invoiceData: any = {};
  dataInvoice: any = {};
  allInvoices: any[] = [];
  comments: any;
  journalData: any;
  public loading: boolean = true;
  private searchSubject = new Subject<string>();
  isAttacheFile = false;
  vat: any = 0;
  canCreateInvoice$ = this.permissionService.hasPermission(
    ModuleIds.Invoice,
    'Create'
  );
  canEditInvoice$ = this.permissionService.hasPermission(
    ModuleIds.Invoice,
    'Edit'
  );
  canCancelInvoice$ = this.permissionService.hasPermission(
    ModuleIds.Invoice,
    'Cancel'
  );
  canApproveRejectInvoice$ = this.permissionService.hasPermission(
    ModuleIds.Invoice,
    'Approve/Reject'
  );
  canSendInvoice$ = this.permissionService.hasPermission(
    ModuleIds.Invoice,
    'Send'
  );

  constructor(
    private router: Router,
    private invoiceService: InvoiceService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((queryParams) => {
      this.invoiceId = queryParams.get('invoiceId');
      this.fetchAllInvoices(); // move this inside to ensure sync
    });

    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchAllInvoices(searchTerm);
      });
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  fetchAllInvoices(search: string = '') {
    this.loading = true;
    const input = {
      search: search,
    };
    this.invoiceService.getAllInvoicesWithPagination(input).subscribe({
      next: (invoices) => {
        this.allInvoices = invoices?.invoices || [];

        if (this.invoiceId) {
          const targetId = +this.invoiceId;
          const index = this.allInvoices.findIndex(
            (c: any) => c.id === targetId
          );
          this.selectedTabIndex = index !== -1 ? index : 0;
          this.invoiceData = this.allInvoices[this.selectedTabIndex] || {};
          this.getInvoiceById(targetId); // ensure latest data
        } else {
          this.selectedTabIndex = 0;
          this.invoiceData = this.allInvoices[0] || {};
        }
      },
      error: (err) => {
        console.error('Error fetching invoices:', err);
      },
    });
  }

  getInvoiceById(id: any) {
    this.invoiceService.listInvoiceById(Number(id)).subscribe({
      next: (data) => {
        this.invoiceData = data;
        this.journalData = this.invoiceData.journalData;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  selectTab(index: number) {
    this.selectedTabIndex = index;
    this.invoiceData = this.allInvoices[index];
    this.journalData = this.invoiceData.journalData;
    if (this.invoiceData) {
      this.router.navigate(['invoice/invoice-details'], {
        queryParams: { invoiceId: this.invoiceData.id },
      });
    }
  }

  isEditableStatus(): boolean {
    const status = this.invoiceData?.invoiceStatus;
    return status !== 'Approved' && status !== 'Rejected' && status !== 'Sent';
  }

  isPdfStatus(): boolean {
    const status = this.invoiceData?.invoiceStatus;
    return status === 'Approved' || status === 'Sent';
  }

  getDebitTotal(): number {
    return (
      this.invoiceData?.journalData?.journalEntries?.reduce(
        (sum: number, entry: any) => sum + (entry.debit || 0),
        0
      ) || 0
    );
  }

  getCreditTotal(): number {
    return (
      this.invoiceData?.journalData?.journalEntries?.reduce(
        (sum: number, entry: any) => sum + (entry.credit || 0),
        0
      ) || 0
    );
  }

  navigateToEditPage(): void {
    const selectedInvoice = this.allInvoices[this.selectedTabIndex];
    const invoiceId = selectedInvoice.id;
    this.router.navigate([`invoice/invoice-edit/${invoiceId}`]);
  }

  onViewDocument() {
    const url = this.invoiceData.invoiceUrl;
    const token = localStorage.getItem('AUTH_TOKEN');
    fetch(url, {
      method: 'GET',
      headers: {
        authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then((response: any) => response.blob())
      .then((blob: any) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch((error: any) => {
        console.error('Error:', error);
      });
  }

  onRemoveInvoice() {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: 'Confirm Cancel',
        message: 'Are you sure you want to cancel this invoice?',
        confirmText: 'Confirm',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.removeInvoice(this.invoiceId);
      }
    });
  }

  private removeInvoice(invoiceId: number): void {
    this.invoiceService.removeInvoiceById(Number(invoiceId)).subscribe({
      next: () => {
        this.toastr.success('Invoice removed successfully.');
        this.router.navigate([`invoice`]);
      },
      error: (error) => {
        this.toastr.error(
          error.message || 'Failed to remove invoice. Please try again.'
        );
      },
    });
  }

  handleApproval(isApproved: boolean, invoiceId: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog, {
      data: {
        title: isApproved ? 'Confirm Approval' : 'Confirm Rejection',
        message: isApproved
          ? 'Are you sure you want to approve this invoice?'
          : 'Are you sure you want to reject this invoice?',
        confirmText: 'Confirm',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.invoiceService
          .approveOrRejectInvoice(Number(invoiceId), isApproved)
          .subscribe({
            next: (res) => {
              console.log('Invoice updated:', res);
              this.toastr.success('Invoice status updated');
              this.fetchAllInvoices();
              this.getInvoiceById(this.invoiceId);
              if (isApproved) {
                this.generatePdf(invoiceId);
              }
            },
            error: (err) => {
              console.error('Error updating invoice status:', err);
            },
          });
      }
    });
  }

  readonly dialog = inject(MatDialog);

  markAsSent() {
    this.invoiceService.markInvoiceAsSent(Number(this.invoiceId)).subscribe({
      next: (res) => {
        this.toastr.success('Invoice status updated');
        this.fetchAllInvoices();
        this.getInvoiceById(this.invoiceId);
      },
      error: (err) => {
        console.error('Error updating invoice status:', err);
      },
    });
  }

  generatePdf(invoiceId: any) {
    this.invoiceService.generateInvoicePdf(Number(invoiceId)).subscribe({
      next: (res) => {
        this.fetchAllInvoices();
        this.getInvoiceById(this.invoiceId);
        this.toastr.success('Invoice pdf successfully generated');
      },
      error: (err) => {
        console.log('Error in generate pdf', err);
      },
    });
  }
}
